import type {
  PlannedExerciseEntry,
  PlannedSet,
  PlannedTargetWeight,
} from "./schemas/plan.js";
import type {
  Program,
  ProgramSession,
  RoundType,
  Slot,
  Variant,
} from "./schemas/program.js";
import { pickAuthoredText } from "./authoredText.js";
import type { Locale } from "./locale.js";

// PE-21 — THE LOCALISATION FRONTIER.
//
// A program carries its free text in one or more languages; a RESOLVED
// session is prescribed to one athlete, who reads one. So the language
// is picked here, once, and everything downstream — Plan, the logger,
// the workout — keeps the plain strings it has always had. Widening
// those would have pushed the per-locale object through half the app
// for no gain: nobody downstream can choose a language.
//
// `locale` is optional: the pure computations over the cycle (muscle
// adjacency, target projection, weekly prescription) resolve text for
// nobody in particular, and pickAuthoredText starts at the author when
// no reader is named.
type TextPicker = (
  value: Parameters<typeof pickAuthoredText>[0],
) => string | undefined;

const textPickerFor = (program: Program, locale?: Locale): TextPicker => {
  const author = program.authorLocale;
  return (value) => pickAuthoredText(value, locale, author) ?? undefined;
};

// Phase 5.3 — week resolution.
//
// Given (program, weekIdx, sessionIdx), produce a list of
// PlannedExerciseEntry that the existing PlanStarter flow can consume.
// The resolution is mechanical: it walks the slot matrix, picks the
// active variant per slot for that week, converts each set's intensity
// into the Plan-shaped target. Percent-of-max stays as a percentage
// (PlansCard's start handler is the place where it's resolved against
// the athlete's live MAX, so a MAX update between resolution and
// "Start session" still shifts the targets correctly).
//
// AMRAP and 1MAX flags propagate as small textual annotations on the
// affected sets / entries. The resolver doesn't transform reps or
// intensity for them; the athlete reads the cue and acts in-session.
//
// All inputs are validated upstream (Zod parse on Program, Plan); the
// only failure modes here are out-of-range indices, returned as null.

export type ResolvedSession = {
  programId: string;
  programName: string;
  weekIdx: number;
  sessionIdx: number;
  /** Stable session id (ProgramSession.id) — MC-8. The writer appends it to
   *  fromPlanId as `/sid<sessionId>` so the logged row survives a reorder. */
  sessionId: string;
  sessionName: string;
  /** Program-level description (Program.description). Display-only coach
   *  context, surfaced read-only in the logger's workout "?" panel — never
   *  written into the saved session (no denormalized snapshot). */
  programDescription?: string;
  /** This day's note (ProgramSession.notes). Same read-only "?" panel; the
   *  per-day coaching guidance the athlete reads at the top of the workout. */
  sessionNotes?: string;
  cycleWeeks: number;
  /** #336 S2: this round's type, when it is not normal. A label: «test»
   *  does not move the 1RM test, which stays on the last round (Q5). */
  roundType?: Exclude<RoundType, "normal">;
  entries: PlannedExerciseEntry[];
};

const variantActiveOnWeek = (v: Variant, weekIdx: number): boolean => {
  if (v.cadence === "every-week") return true;
  const cycleLen = v.cadence === "alternating" ? 2 : 3;
  // Modulo with positive result, even when (weekIdx - phaseOffset)
  // goes negative for early weeks of an offset variant.
  const offset = ((weekIdx - v.phaseOffset) % cycleLen + cycleLen) % cycleLen;
  return offset === 0;
};

// Pick the first variant that's active on the given week. Returns null
// when no variant qualifies — caller skips the slot entirely. The old
// path silently fell back to variants[0], which surfaced the wrong
// exercise on weeks the coach explicitly marked "off" via cadence +
// phaseOffset on an ill-formed single-variant slot. The "skip slots
// where no variant is active" comment in buildSessionEntries is the
// intended contract; this enforces it.
const pickActiveVariant = (slot: Slot, weekIdx: number): Variant | null => {
  for (const v of slot.variants) {
    if (variantActiveOnWeek(v, weekIdx)) return v;
  }
  return null;
};

const intensityToTargetWeight = (
  intensity: Slot["weeks"][number]["sets"][number]["intensity"],
): {
  targetWeight: PlannedTargetWeight;
  targetRpe: number | undefined;
  targetRir: number | undefined;
} => {
  if (intensity.kind === "absolute") {
    return {
      targetWeight: { kind: "absolute", weightKg: intensity.weightKg },
      targetRpe: undefined,
      targetRir: undefined,
    };
  }
  if (intensity.kind === "percent-of-max") {
    return {
      targetWeight: { kind: "percentage", pctOfMax: intensity.pct },
      targetRpe: undefined,
      targetRir: undefined,
    };
  }
  if (intensity.kind === "history") {
    return {
      targetWeight: { kind: "history" },
      targetRpe: undefined,
      targetRir: undefined,
    };
  }
  if (intensity.kind === "rpe") {
    return {
      targetWeight: { kind: "none" },
      targetRpe: intensity.target,
      targetRir: undefined,
    };
  }
  if (intensity.kind === "rir") {
    return {
      targetWeight: { kind: "none" },
      targetRpe: undefined,
      targetRir: intensity.target,
    };
  }
  return {
    targetWeight: { kind: "none" },
    targetRpe: undefined,
    targetRir: undefined,
  };
};

const joinNotes = (parts: (string | undefined | false)[]): string | undefined => {
  const kept = parts.filter((p): p is string => typeof p === "string" && p.trim().length > 0);
  if (kept.length === 0) return undefined;
  return kept.join(" — ");
};

// Build the planned sets for a slot's given week. Applies the
// AMRAP-on-last-set transformation when the slot's flag is set, with
// 1MAX taking precedence on the test week's last set: a coach who
// configured both maxAttempt and amrapLastSet on the same slot wants
// a max-effort single, not a max-rep set, on the cycle's final
// session. `isTestWeek` is the cycle's last week (cycleWeeks - 1).
//
// AMRAP shape (S4): the prescribed reps stay on `targetReps` as a
// floor, and the structured `amrap` flag lets the logger render
// "X+ reps". The old behaviour forced targetReps = 0 and appended
// "AMRAP" to notes — that wiped the coach's rep floor and hid the
// signal in free text.
const buildPlannedSets = (
  slot: Slot,
  weekIdx: number,
  cycleWeeks: number,
  text: TextPicker,
): PlannedSet[] => {
  const week = slot.weeks[weekIdx];
  if (!week) return [];
  const lastIdx = week.sets.length - 1;
  const isTestWeek = weekIdx === cycleWeeks - 1;
  return week.sets.map((set, i) => {
    const onLastSet = i === lastIdx;
    // Per-set amrap (SetPrescription.amrap, the "5+" syntax) ORs with
    // the older slot-level last-set flag. reps stays the floor.
    const wantsAmrap =
      set.amrap === true || (slot.amrapLastSet === true && onLastSet);
    const wantsMaxAttempt =
      slot.maxAttempt === true && isTestWeek && onLastSet;
    // 1MAX wins (S13). The two flags are conceptually mutually
    // exclusive on the same set; clamp to maxAttempt when both fire.
    const isAmrap = wantsAmrap && !wantsMaxAttempt;
    const { targetWeight, targetRpe, targetRir } = intensityToTargetWeight(
      set.intensity,
    );
    const planned: PlannedSet = {
      targetReps: set.reps,
      targetWeight,
    };
    if (targetRpe !== undefined) planned.targetRpe = targetRpe;
    if (targetRir !== undefined) planned.targetRir = targetRir;
    if (isAmrap) planned.amrap = true;
    // #336 S3: the range rides beside the target, never on an AMRAP (Q6).
    if (!isAmrap && set.repsMin !== undefined && set.repsMax !== undefined) {
      planned.repsMin = set.repsMin;
      planned.repsMax = set.repsMax;
    }
    // Time axis pass-through (HI-1). Copied verbatim, NOT interpreted:
    // whether a countdown bout auto-advances is the executor's call,
    // and resolution deciding it here would be a second opinion.
    if (set.work !== undefined) planned.work = set.work;
    if (set.restSec !== undefined) planned.restSec = set.restSec;
    const setNotes = text(set.notes);
    if (setNotes) planned.notes = setNotes;
    return planned;
  });
};

// Build the planned-exercise entries for a single session at a given
// week. Skips slots where no variant is active for that week (rare with
// well-formed cadences) and slots with no sets configured for that
// week's plan.
const buildSessionEntries = (
  session: ProgramSession,
  weekIdx: number,
  cycleWeeks: number,
  text: TextPicker,
): PlannedExerciseEntry[] => {
  const isTestWeek = weekIdx === cycleWeeks - 1;
  const out: PlannedExerciseEntry[] = [];
  for (const slot of session.slots) {
    const variant = pickActiveVariant(slot, weekIdx);
    if (!variant) continue;
    const sets = buildPlannedSets(slot, weekIdx, cycleWeeks, text);
    if (sets.length === 0) continue;
    const entryNotes = joinNotes([
      text(slot.instructions),
      slot.maxAttempt === true && isTestWeek && "1MAX attempt week",
    ]);
    const entry: PlannedExerciseEntry = {
      exerciseId: variant.exerciseId,
      sets,
    };
    if (variant.exerciseName) entry.exerciseName = variant.exerciseName;
    if (entryNotes) entry.notes = entryNotes;
    // Propagate the slot-level RPE-column default onto the resolved
    // entry. Logger slice 3 reads `entry.rpeEnabled` with no awareness
    // of whether the session originated from a Plan or a Program slot.
    if (slot.rpeEnabled === true) entry.rpeEnabled = true;
    if (slot.rirEnabled === true) entry.rirEnabled = true;
    if (slot.group) {
      entry.group = {
        id: slot.group.id,
        kind: slot.group.kind,
        ...(slot.group.name !== undefined ? { name: slot.group.name } : {}),
      };
    }
    out.push(entry);
  }
  // Drop singleton groups — same sanitiser the workout logger and plan
  // editor run. A program where the coach only marked one slot as
  // "Superset A" would otherwise leak a stray group label into the
  // resolved plan.
  const counts = new Map<string, number>();
  for (const e of out) {
    if (e.group) counts.set(e.group.id, (counts.get(e.group.id) ?? 0) + 1);
  }
  for (const e of out) {
    if (e.group && (counts.get(e.group.id) ?? 0) < 2) delete e.group;
  }
  // Force symmetric supersets (Antenore 2026-06-01, inbox 11f01866 pt 2). A
  // superset renders as interleaved rounds (1A·1B·2A·2B…), so members with
  // different set counts leave orphaned rounds. Pad each grouped member UP to
  // its group's longest member by repeating that member's own last planned set
  // — never trim (dropping a prescribed set would silently lose volume). This
  // is the read-time safety net; the web editor also guards at authoring time,
  // and existing asymmetric programs were data-fixed. No-op when every group is
  // already uniform.
  const groupTarget = new Map<string, number>();
  for (const e of out) {
    if (!e.group) continue;
    const cur = groupTarget.get(e.group.id) ?? 0;
    if (e.sets.length > cur) groupTarget.set(e.group.id, e.sets.length);
  }
  for (const e of out) {
    if (!e.group || e.sets.length === 0) continue;
    const target = groupTarget.get(e.group.id) ?? e.sets.length;
    while (e.sets.length < target) {
      // Deep clone (never a shallow spread, PE-53): nested fields like
      // targetWeight must not stay aliased between the source set and the
      // padded copy. And a padded copy is a normal set, never AMRAP, even
      // when the set it copies is the group's last (and possibly AMRAP)
      // set: the coach only meant ONE max-effort set, not one per pad. No
      // rep range either (#336 S3): nobody wrote one for this set.
      const source = e.sets[e.sets.length - 1]!;
      // Tripwire: this is a field-wise clone, not structural. A new
      // PlannedSet nested-object field needs its own deep-copy line here
      // too, or the outer spread silently aliases it back to `source`,
      // and nothing will fail to compile if it is forgotten. ⛔ Not
      // `structuredClone`: shared's lib is ES2022 (no structuredClone),
      // and Hermes support for such globals has bitten before
      // (crypto.randomUUID, see uuid.ts).
      const copy: PlannedSet = {
        ...source,
        targetWeight: { ...source.targetWeight },
      };
      if (source.work !== undefined) copy.work = { ...source.work };
      delete copy.amrap;
      delete copy.repsMin;
      delete copy.repsMax;
      e.sets.push(copy);
    }
  }
  return out;
};

// True when this session yields at least one entry for the given week:
// some slot has an active variant AND a non-empty set list. The inverse
// — a (session, week) cell whose slots are all inactive (sets: [],
// scheduler-composer week-subset blocks) or cadence-skipped — must not
// be proposed by next-session math, or the athlete would be offered an
// empty workout.
export const sessionHasEntriesForWeek = (
  session: ProgramSession,
  weekIdx: number,
): boolean =>
  session.slots.some(
    (slot) =>
      pickActiveVariant(slot, weekIdx) !== null &&
      (slot.weeks[weekIdx]?.sets.length ?? 0) > 0,
  );

// The whole repeating macrocycle resolved to an ordered day sequence: every
// (week, session) cell of the cycle, week-major, each through the SAME
// active-variant picker `resolveProgramSession` runs. The shared walk behind
// any "look at every day the program prescribes" consumer (muscle-adjacency,
// weekly prescription), so the cycle traversal + variant resolution lives once.
// A cell whose slots are all inactive that week resolves to a session with
// empty `entries` (kept, not dropped — it's still a scheduled day).
export const resolveFullCycleSessions = (
  program: Program,
  locale?: Locale,
): ResolvedSession[] => {
  const out: ResolvedSession[] = [];
  for (let weekIdx = 0; weekIdx < program.cycleWeeks; weekIdx++) {
    for (let sessionIdx = 0; sessionIdx < program.sessions.length; sessionIdx++) {
      const resolved = resolveProgramSession({
        program,
        weekIdx,
        sessionIdx,
        locale,
      });
      if (resolved) out.push(resolved);
    }
  }
  return out;
};

// MC-8 — resolve a program session's CURRENT index from its stable id,
// falling back to a positional index when the id is absent (pre-MC-8 rows) or
// no longer resolves (session deleted). The single home for the
// id-wins-over-ordinal rule, shared by the logged-row resolver below and the
// "next up" scheduler (programSchedule.nextSlotFor).
export const resolveSessionIndexById = (
  sessions: ReadonlyArray<{ id: string }>,
  id: string | undefined | null,
  fallbackIdx: number,
): number => {
  if (id != null) {
    const byId = sessions.findIndex((s) => s.id === id);
    if (byId >= 0) return byId;
  }
  return fallbackIdx;
};

export const resolveProgramSession = (params: {
  program: Program;
  weekIdx: number; // 0-based
  sessionIdx: number; // 0-based
  // MC-8 — when present, the stable session id wins over the positional
  // index: a reordered program still resolves a logged row to the session
  // the athlete actually ran. Falls back to sessionIdx for pre-MC-8 rows
  // and when the id no longer resolves (session deleted).
  sessionId?: string;
  // Reader's language (PE-21). Omitted by the pure cycle computations,
  // which resolve text for nobody in particular.
  locale?: Locale;
}): ResolvedSession | null => {
  const { program, weekIdx, sessionIdx, sessionId, locale } = params;
  if (weekIdx < 0 || weekIdx >= program.cycleWeeks) return null;
  const resolvedIdx = resolveSessionIndexById(
    program.sessions,
    sessionId,
    sessionIdx,
  );
  const session = program.sessions[resolvedIdx];
  if (!session) return null;
  const text = textPickerFor(program, locale);
  const entries = buildSessionEntries(
    session,
    weekIdx,
    program.cycleWeeks,
    text,
  );
  const programName = text(program.name);
  const sessionName = text(session.name);
  const programDescription = text(program.description);
  const sessionNotes = text(session.notes);
  const roundType = program.roundTypes?.[weekIdx];
  return {
    programId: program.id,
    // requiredAuthoredText guarantees at least one language carries
    // text, so the picker only returns undefined on a row that predates
    // the guarantee. Empty string keeps the field's `string` contract
    // rather than pushing an optional through every consumer.
    programName: programName ?? "",
    weekIdx,
    // The RESOLVED index (id wins over the input ordinal when it resolves),
    // so callers persist / display the session's current position.
    sessionIdx: resolvedIdx,
    sessionId: session.id,
    sessionName: sessionName ?? "",
    ...(programDescription ? { programDescription } : {}),
    ...(sessionNotes ? { sessionNotes } : {}),
    cycleWeeks: program.cycleWeeks,
    ...(roundType && roundType !== "normal" ? { roundType } : {}),
    entries,
  };
};
